import { Option, Value } from 'baseui/select';
import { useFormState } from 'components/elements/form';
import deleteColumn from 'components/elements/table/editable-columns/delete-column';
import TableInput from 'components/elements/table/table-input';
import { ColumnDataType } from 'components/elements/table/type';
import Text from 'components/elements/text';
import { SeparatorVertical } from 'legacy-modules/styles';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { addNewDocumentPurchaseAddOns } from '../../hooks/use-get-default-values';
import { DocumentPurchaseItemAddOnsFormType } from '../form-type';

function useGetAddOnsTableList(parentIndex: number) {
  const { t } = useTranslation();
  const { control } = useFormContext();
  const { editable } = useFormState();
  const guestNameField =
    `documentPurchases.${parentIndex}.documentPurchaseGuests` as const;
  const [guests] = useWatch({
    control,
    name: [guestNameField],
  });

  const guestList: Option[] = guests
    .filter((guest) => guest.guestId)
    .map((guest) => {
      return {
        id: guest.guestId,
        label: guest.guestName,
      };
    });

  return [
    {
      header: t('common:name'),
      accessorKey: 'guestId',
      minSize: 100,
      dataType: ColumnDataType.select,
      options: guestList,
    },
    {
      header: t('common:description'),
      accessorKey: 'description',
      minSize: 300,
      dataType: ColumnDataType.text,
    },
    {
      header: t('common:price'),
      accessorKey: 'price',
      minSize: 100,
      dataType: ColumnDataType.number,
      minNumber: 0,
    },
    ...deleteColumn(editable),
  ];
}

const AddOnsTable = (props: { parentIndex: number }) => {
  const { t } = useTranslation();
  const { editable } = useFormState();
  const { getValues, setValue, trigger, clearErrors, control } =
    useFormContext();

  const name =
    `documentPurchases.${props.parentIndex}.documentPurchaseAddOns` as const;
  const addons = useWatch({
    name,
    control,
  }) as DocumentPurchaseItemAddOnsFormType[];
  const columns = useGetAddOnsTableList(props.parentIndex);

  const dispatch = React.useCallback(
    (action) => {
      const currentRowIndex = action.rowIndex;
      const addOns = getValues(name) as DocumentPurchaseItemAddOnsFormType[];
      const addOn = addOns[currentRowIndex];
      const columnId =
        action.columnId as keyof DocumentPurchaseItemAddOnsFormType;
      const extra = action.extra as Value;

      const otherVal: () => Partial<DocumentPurchaseItemAddOnsFormType> =
        () => {
          if (columnId === 'guestId') {
            return { guestName: extra?.[0]?.label as string };
          }
          return {};
        };

      if (action.type === 'addRow') {
        const data = [...addOns, addNewDocumentPurchaseAddOns()];
        setValue(name, data);
        trigger(name);
      } else if (action.type === 'removeRow') {
        addOns.splice(currentRowIndex, 1);
        setValue(name, addOns);
        trigger(name);
      } else if (action.type === 'update_cell') {
        addOns[currentRowIndex] = {
          ...addOn,
          [action.columnId]: action.value,
          ...otherVal(),
        };
        setValue(name, addOns);
        clearErrors(`${name}.${currentRowIndex}.${columnId}`);
      }
    },
    [clearErrors, getValues, name, setValue, trigger],
  );

  if ((addons || []).length || editable) {
    return (
      <div style={{ width: '100%' }}>
        <Text variant="ParagraphMedium">{t('ticketSales:add_ons')}</Text>
        <SeparatorVertical $margin={24} />
        <TableInput
          columns={columns}
          data={addons}
          dispatch={dispatch}
          disabled={!editable}
          name={name}
          uniqueRowKey="customId"
        />
      </div>
    );
  }
  return <></>;
};

export default AddOnsTable;
